import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
} from "react";
import {
  AdminApiError,
  deactivateAdminDepartment,
  getAdminDepartmentUsage,
  reactivateAdminDepartment,
  type DepartmentAdminResponse,
  type DepartmentUsageSummary,
} from "../../api/admin";
import "./DepartmentLifecyclePanel.css";

export type DepartmentLifecycleAction = "deactivate" | "reactivate";

export type DepartmentLifecyclePanelProps = {
  department: DepartmentAdminResponse;
  action: DepartmentLifecycleAction;
  disabled: boolean;
  onCompleted: () => Promise<void>;
  onCancel: () => void;
};

type UsageState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; summary: DepartmentUsageSummary }
  | { kind: "error"; message: string };

type UiState = {
  isSubmitting: boolean;
  usage: UsageState;
  formError: string;
};

function initialUi(): UiState {
  return {
    isSubmitting: false,
    usage: { kind: "idle" },
    formError: "",
  };
}

export default function DepartmentLifecyclePanel(
  props: DepartmentLifecyclePanelProps,
) {
  const { department, action, disabled, onCompleted, onCancel } = props;
  const [ui, setUi] = useState<UiState>(initialUi);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const confirmButtonRef = useRef<HTMLButtonElement | null>(null);
  const isMountedRef = useRef(true);
  const usageInFlightRef = useRef(false);
  const submitInFlightRef = useRef(false);

  const headingId = useId();
  const descriptionId = useId();
  const contextListId = useId();
  const usageHeadingId = useId();
  const usageListId = useId();
  const usageLoadingId = useId();
  const usageErrorId = useId();
  const blockingId = useId();
  const formErrorId = useId();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    headingRef.current?.focus();
  }, [action, department.id]);

  const verb = action === "deactivate" ? "Deactivate" : "Reactivate";
  const pastTense = action === "deactivate" ? "Deactivated" : "Reactivated";
  const isDeactivate = action === "deactivate";

  const clearNotifications = useCallback(() => {
    setUi((prev) => ({
      ...prev,
      formError: "",
    }));
  }, []);

  const loadUsage = useCallback(async () => {
    if (usageInFlightRef.current) {
      return;
    }
    if (!isMountedRef.current) {
      return;
    }
    usageInFlightRef.current = true;
    setUi((prev) => ({
      ...prev,
      usage: { kind: "loading" },
      formError: "",
    }));
    try {
      const summary = await getAdminDepartmentUsage(department.id);
      if (!isMountedRef.current) {
        return;
      }
      setUi((prev) => ({
        ...prev,
        usage: { kind: "ready", summary },
      }));
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      if (error instanceof AdminApiError) {
        setUi((prev) => ({
          ...prev,
          usage: {
            kind: "error",
            message: error.detail || "Unable to load Department usage.",
          },
        }));
        return;
      }
      setUi((prev) => ({
        ...prev,
        usage: {
          kind: "error",
          message: "Unable to load Department usage. Please try again.",
        },
      }));
    } finally {
      usageInFlightRef.current = false;
    }
  }, [department.id]);

  useEffect(() => {
    if (!isDeactivate) {
      setUi((prev) => ({ ...prev, usage: { kind: "idle" } }));
      return;
    }
    if (disabled) {
      return;
    }
    void loadUsage();
  }, [action, department.id, disabled, isDeactivate, loadUsage]);

  const handleRetryUsage = useCallback(() => {
    if (disabled || ui.isSubmitting) {
      return;
    }
    setUi((prev) => ({ ...prev, formError: "" }));
    void loadUsage();
  }, [disabled, loadUsage, ui.isSubmitting]);

  const usageReady = ui.usage.kind === "ready";
  const usageSummary: DepartmentUsageSummary | null =
    ui.usage.kind === "ready" ? ui.usage.summary : null;
  const usageBlocking =
    isDeactivate &&
    usageSummary !== null &&
    (usageSummary.active_users > 0 ||
      usageSummary.active_routing_request_types > 0 ||
      usageSummary.open_tickets > 0);

  const handleConfirm = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitInFlightRef.current || disabled) {
        return;
      }
      clearNotifications();
      if (isDeactivate) {
        if (ui.usage.kind !== "ready") {
          return;
        }
        const summary = ui.usage.summary;
        if (
          summary.active_users > 0 ||
          summary.active_routing_request_types > 0 ||
          summary.open_tickets > 0
        ) {
          return;
        }
      }
      submitInFlightRef.current = true;
      setUi((prev) => ({ ...prev, isSubmitting: true }));
      try {
        if (isDeactivate) {
          await deactivateAdminDepartment(department.id);
        } else {
          await reactivateAdminDepartment(department.id);
        }
        if (!isMountedRef.current) {
          return;
        }
        setUi((prev) => ({
          ...prev,
          isSubmitting: false,
        }));
        await onCompleted();
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        if (error instanceof AdminApiError) {
          setUi((prev) => ({
            ...prev,
            isSubmitting: false,
            formError: error.detail,
          }));
          return;
        }
        setUi((prev) => ({
          ...prev,
          isSubmitting: false,
          formError:
            action === "deactivate"
              ? "Unable to deactivate the department. Please try again."
              : "Unable to reactivate the department. Please try again.",
        }));
      } finally {
        if (isMountedRef.current) {
          submitInFlightRef.current = false;
        }
      }
    },
    [
      action,
      clearNotifications,
      department.id,
      disabled,
      isDeactivate,
      onCompleted,
      ui.usage,
    ],
  );

  const allInputsDisabled =
    disabled || ui.isSubmitting || (isDeactivate && !usageReady) || usageBlocking;
  const cancelDisabled = ui.isSubmitting;

  return (
    <section
      className="department-lifecycle-panel"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      id={`department-lifecycle-panel-${department.id}-${action}`}
    >
      <h2
        id={headingId}
        className="department-lifecycle-panel__title"
        ref={headingRef}
        tabIndex={-1}
      >
        {verb} Department
      </h2>
      <p
        id={descriptionId}
        className="department-lifecycle-panel__description"
      >
        {isDeactivate
          ? "Deactivating this Department hides it from operational lists and disables ticket routing."
          : "Reactivating this Department returns it to Admin and eligible operational use according to its routing configuration."}
      </p>

      <div className="department-lifecycle-panel__field">
        <span className="department-lifecycle-panel__label">Read-only context</span>
        <dl
          id={contextListId}
          className="department-lifecycle-panel__context"
          aria-label="Read-only Department context"
        >
          <div className="department-lifecycle-panel__context-row">
            <dt className="department-lifecycle-panel__context-label">Name</dt>
            <dd className="department-lifecycle-panel__context-value">{department.name}</dd>
          </div>
          <div className="department-lifecycle-panel__context-row">
            <dt className="department-lifecycle-panel__context-label">Code</dt>
            <dd className="department-lifecycle-panel__context-value">{department.code}</dd>
          </div>
          <div className="department-lifecycle-panel__context-row">
            <dt className="department-lifecycle-panel__context-label">Company</dt>
            <dd className="department-lifecycle-panel__context-value">
              {department.company_name ?? "Unknown company"}
            </dd>
          </div>
          <div className="department-lifecycle-panel__context-row">
            <dt className="department-lifecycle-panel__context-label">Current status</dt>
            <dd className="department-lifecycle-panel__context-value">
              {department.is_active ? "Active" : "Inactive"}
            </dd>
          </div>
        </dl>
      </div>

      {isDeactivate ? (
        <section className="department-lifecycle-panel__usage" aria-labelledby={usageHeadingId}>
          <h3
            id={usageHeadingId}
            className="department-lifecycle-panel__usage-heading"
          >
            Usage summary
          </h3>
          {ui.usage.kind === "loading" ? (
            <p
              id={usageLoadingId}
              className="department-lifecycle-panel__status"
              role="status"
              aria-live="polite"
            >
              Loading Department usage…
            </p>
          ) : null}
          {ui.usage.kind === "ready" ? (
            <dl
              id={usageListId}
              className="department-lifecycle-panel__usage-list"
              aria-labelledby={usageHeadingId}
            >
              <div className="department-lifecycle-panel__usage-row">
                <dt className="department-lifecycle-panel__usage-label">Active users</dt>
                <dd
                  className={
                    "department-lifecycle-panel__usage-value" +
                    (ui.usage.summary.active_users > 0
                      ? " department-lifecycle-panel__usage-value--blocking"
                      : "")
                  }
                >
                  {ui.usage.summary.active_users}
                </dd>
              </div>
              <div className="department-lifecycle-panel__usage-row">
                <dt className="department-lifecycle-panel__usage-label">
                  Active routing Request Categories
                </dt>
                <dd
                  className={
                    "department-lifecycle-panel__usage-value" +
                    (ui.usage.summary.active_routing_request_types > 0
                      ? " department-lifecycle-panel__usage-value--blocking"
                      : "")
                  }
                >
                  {ui.usage.summary.active_routing_request_types}
                </dd>
              </div>
              <div className="department-lifecycle-panel__usage-row">
                <dt className="department-lifecycle-panel__usage-label">Open tickets</dt>
                <dd
                  className={
                    "department-lifecycle-panel__usage-value" +
                    (ui.usage.summary.open_tickets > 0
                      ? " department-lifecycle-panel__usage-value--blocking"
                      : "")
                  }
                >
                  {ui.usage.summary.open_tickets}
                </dd>
              </div>
              <div className="department-lifecycle-panel__usage-row">
                <dt className="department-lifecycle-panel__usage-label">Total tickets</dt>
                <dd className="department-lifecycle-panel__usage-value">
                  {ui.usage.summary.total_tickets}
                </dd>
              </div>
            </dl>
          ) : null}
          {ui.usage.kind === "error" ? (
            <div className="department-lifecycle-panel__usage-error-wrap">
              <p
                id={usageErrorId}
                className="department-lifecycle-panel__form-error"
                role="alert"
              >
                {ui.usage.message}
              </p>
              <button
                type="button"
                className="department-lifecycle-panel__retry"
                onClick={handleRetryUsage}
                disabled={disabled || ui.isSubmitting}
              >
                Retry
              </button>
            </div>
          ) : null}
          {usageBlocking ? (
            <p
              id={blockingId}
              className="department-lifecycle-panel__blocking"
              role="alert"
            >
              The Department cannot be deactivated while it has active users, active
              routing Request Categories, or open tickets. Resolve all blocking
              dependencies first.
            </p>
          ) : null}
        </section>
      ) : null}

      <form
        noValidate
        className="department-lifecycle-panel__form"
        onSubmit={handleConfirm}
      >
        {ui.formError.length > 0 ? (
          <p
            id={formErrorId}
            className="department-lifecycle-panel__form-error"
            role="alert"
          >
            {ui.formError}
          </p>
        ) : null}


        <div
          className="department-lifecycle-panel__actions"
          role="group"
          aria-labelledby={headingId}
        >
          <button
            ref={confirmButtonRef}
            type="submit"
            className={
              "department-lifecycle-panel__confirm" +
              (action === "reactivate"
                ? " department-lifecycle-panel__confirm--reactivate"
                : "")
            }
            disabled={allInputsDisabled}
            aria-busy={ui.isSubmitting}
          >
            {ui.isSubmitting
              ? action === "deactivate"
                ? "Deactivating…"
                : "Reactivating…"
              : action === "deactivate"
                ? "Deactivate Department"
                : "Reactivate Department"}
          </button>
          <button
            type="button"
            className="department-lifecycle-panel__cancel"
            onClick={onCancel}
            disabled={cancelDisabled}
          >
            Cancel
          </button>
        </div>
        <span
          className="department-lifecycle-panel__visually-hidden"
          aria-live="polite"
        >
          {action === "deactivate"
            ? pastTense + " confirmation is final."
            : pastTense + " confirmation is final."}
        </span>
      </form>
    </section>
  );
}
